import React, { useEffect, useState, useCallback } from "react";
import { Base_URL } from "../url";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
export default function InventoryScreen() {
  const {user}=useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [approvingIds, setApprovingIds] = useState({}); // { [receiptId]: true }
  const navigate = useNavigate();

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${Base_URL}/Inventory`);
      const data = res.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : [];
      setItems(list);
    } catch (err) {
      setError(err.message || "Failed to fetch inventory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const filtered = items.filter((it) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (it.inventoryReceiptNumber || "").toLowerCase().includes(q) ||
      (it.purchaseOrderNumber || "").toLowerCase().includes(q) ||
      (it.poTypeName || "").toLowerCase().includes(q)
    );
  });

  const handleApprove = async (id) => {
    if (!id) return;
    // mark as approving
    setApprovingIds((s) => ({ ...s, [id]: true }));
    setError(null);
    try {
      // call your approve endpoint (PUT)
      // Using params is cleaner than building query string manually
      await axios.put(`${Base_URL}/Inventory/id`,JSON.stringify(user.userId), { params: { id }, headers: { "Content-Type": "application/json" } });
      // after success reload the inventory
      await fetchInventory();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Approve failed");
    } finally {
      // unset approving
      setApprovingIds((s) => {
        const copy = { ...s };
        delete copy[id];
        return copy;
      });
    }
  };

  const uniqueByReceipt = (() => {
    const map = new Map();
    for (const r of filtered) {
      const key = r.inventoryReceiptNumber ?? r.inventoryReceiptId ?? `id-${r.id}`;
      if (!map.has(key)) map.set(key, r);
    }
    return Array.from(map.values());
  })();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-semibold mb-4">Inventory Receipts</h1>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b flex items-center gap-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by IR number, PO number, PO type..."
              className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring"
            />
            <div className="text-sm text-gray-600">
              {loading ? "Loading..." : `${uniqueByReceipt.length} receipts`}
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-sm divide-y">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left">IR Number</th>
                  <th className="p-3 text-left">PO Number</th>
                  <th className="p-3 text-left">Inventory Type</th>
                  <th className="p-3 text-left">Movement</th>
                  <th className="p-3 text-left">Receipt Date</th>
                  <th className="p-3 text-left">Received By</th>
                  <th className="p-3 text-left">Approve Status</th>
                  <th className="p-3 text-left">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y bg-white">
                {uniqueByReceipt.map((row, idx) => {
                  const receiptId = row.inventoryReceiptNumber ?? row.inventoryReceiptId ?? "";
                  const isApproving = !!approvingIds[receiptId];
                  return (
                    <tr key={receiptId || idx} className="hover:bg-gray-50">
                      <td className="p-3">{receiptId || "-"}</td>
                      <td className="p-3">{row.purchaseOrderNumber ?? "-"}</td>
                      <td className="p-3">{row.poTypeName ?? "-"}</td>
                      <td className="p-3">{row.movementTypeName ?? "-"}</td>
                      <td className="p-3">
                        {row.receiptCreatedDate ? row.receiptCreatedDate.split("T")[0] : "-"}
                      </td>
                      <td className="p-3">{row.receivedBy ?? "-"}</td>
                      <td className="p-3">{row.approveStatus ?? "-"}</td>
                      <td className="p-3">
                        <button
                          onClick={() => {
                            if (receiptId) navigate(`/inventory/${encodeURIComponent(receiptId)}`);
                          }}
                          className="px-3 py-1 rounded bg-indigo-600  text-sm hover:opacity-90"
                        >
                          View
                        </button>
                        <button
  onClick={() => handleApprove(receiptId)}
  disabled={isApproving || (row.approveStatus?.toLowerCase() === "confirmed")}
  className={`px-3 mx-3 py-1 rounded text-sm ${
    isApproving
      ? "bg-gray-300 text-gray-700 cursor-not-allowed"
      : row.approveStatus?.toLowerCase() === "confirmed"
      ? "bg-green-300 text-gray-700 cursor-not-allowed"
      : "bg-green-600  hover:opacity-90"
  }`}
>
  {isApproving
    ? "Approving..."
    : row.approveStatus?.toLowerCase() === "confirmed"
    ? "Approved"
    : "Approve"}
</button>

                      </td>
                    </tr>
                  );
                })}

                {!loading && uniqueByReceipt.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-500">
                      No receipts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {error && <div className="mt-4 text-sm text-red-600">Error: {error}</div>}
      </div>
    </div>
  );
}
